import type { ResolvedReference } from './references.ts';

export type MediaReference = ResolvedReference & { expiresAt?: number };
/** Read-only host capabilities used by administrator inspection views. The
 * adapter is deliberately opaque to the renderer: it may return only a
 * server-issued GET URL and is never allowed to mint a normal media ticket. */
export interface ReadOnlyResourceAdapter {
  mediaUrl: (reference: ResolvedReference, signal?: AbortSignal) => Promise<string | null> | string | null;
  artifactUrl?: (path: string) => Promise<string | null> | string | null;
  figureUrl?: (receiptId: string, outputId: string) => Promise<string | null> | string | null;
}
/** The authenticated app mints one short-lived, read-only resource capability.
 * Media bytes remain streamed by the server; no video is buffered into JS. */
export class MediaTicketCache {
  private entries = new Map<string, Promise<MediaReference>>();
  private controller = new AbortController();
  constructor(private origin: string, private request: typeof fetch = fetch.bind(globalThis), private readOnly?: ReadOnlyResourceAdapter) {}
  async resolve(reference: ResolvedReference, refresh = false): Promise<MediaReference> {
    if (reference.kind !== 'image' && reference.kind !== 'video') return reference;
    const key = reference.target.imageGenerationReceiptId
      ? JSON.stringify([reference.target.imageGenerationReceiptId, reference.target.imageGenerationOutputId])
      : reference.target.fileId && reference.contentRevision ? JSON.stringify([reference.target.fileId, reference.contentRevision]) : reference.resourceUrl;
    const existing = this.entries.get(key);
    if (existing && !refresh) {
      const value = await existing;
      if (value.expiresAt! > Date.now() + 30_000) return { ...reference, resourceUrl: value.resourceUrl, expiresAt: value.expiresAt };
    }
    const promise = this.mint(reference);
    this.entries.set(key, promise);
    while (this.entries.size > 64) this.entries.delete(this.entries.keys().next().value!);
    promise.catch(() => { if (this.entries.get(key) === promise) this.entries.delete(key); });
    return promise;
  }
  private async mint(reference: ResolvedReference): Promise<MediaReference> {
    if (this.readOnly) {
      const raw = await this.readOnly.mediaUrl(reference, this.controller.signal);
      if (!raw) throw new Error('只读媒体不可用');
      const url = new URL(raw, this.origin);
      if (url.origin !== this.origin || url.username || url.password || url.search || url.hash
        || !/^\/api\/admin\/inspection-media\/[A-Za-z0-9_-]+$/.test(url.pathname)) throw new Error('只读媒体地址无效');
      return { ...reference, resourceUrl: url.href, expiresAt: Date.now() + 60_000 };
    }
    const { imageGenerationReceiptId, imageGenerationOutputId } = reference.target;
    if ((imageGenerationReceiptId || imageGenerationOutputId) && (!imageGenerationReceiptId || !imageGenerationOutputId)) throw new Error('生成图片身份不完整');
    const response = await this.request(new URL('/api/media-tickets',this.origin), { method: 'POST', credentials: 'same-origin',
      signal: this.controller.signal, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(imageGenerationReceiptId ? {imageGenerationReceiptId,outputId:imageGenerationOutputId}
        : reference.target.fileId ? {fileId:reference.target.fileId,...(reference.contentRevision?{contentRevision:reference.contentRevision}:{})} : {path:reference.target.path}) });
    if (!response.ok) throw new Error(`媒体访问授权失败 (${response.status})`);
    const value = await response.json() as {url?:unknown;expiresAt?:unknown};
    if (typeof value.url !== 'string') throw new Error('媒体访问地址无效');
    const url = new URL(value.url,this.origin);
    const expiresAt = typeof value.expiresAt === 'number' ? value.expiresAt : typeof value.expiresAt === 'string' ? Date.parse(value.expiresAt) : NaN;
    if (url.origin !== this.origin || !/^\/api\/media\/[A-Za-z0-9_-]+$/.test(url.pathname) || url.search || url.hash
      || url.username || url.password || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw new Error('媒体访问授权无效');
    return {...reference,resourceUrl:url.href,expiresAt};
  }
  dispose() { this.controller.abort(); this.entries.clear(); }
}
